import type BoundsUtil from "../../boundsUtil";
import type MatrixUtil from "../../matrixUtil";
import type GeomUtil from "../geomUtil";
import type { GeomTypeUtil } from "./geomTypeUtil";

/**
 * Geometry functions for multi polygon
 */
export default class MP implements GeomTypeUtil {
    private readonly boundsUtil: BoundsUtil;
    private readonly geomUtil: GeomUtil;
    private readonly matrixUtil: MatrixUtil;

    constructor(boundsUtil: BoundsUtil, geomUtil: GeomUtil, matrixUtil: MatrixUtil) {
        this.boundsUtil = boundsUtil;
        this.geomUtil = geomUtil;
        this.matrixUtil = matrixUtil;
    }

    /**
     * 
     * @param geom 
     * @param callback 
     * @param owner 
     */
    extractPoints(geom: any[], callback: Function, owner?: any) {
        for (let g = geom.length; g-- > 1; ) {
            const polygon = geom[g];

            for (let p = polygon.length; p-- > 0; ) {
                const ring = polygon[p];

                for (let r = ring.length - 2; r >= 2; r -= 2) {
                    callback.call(owner, ring, r);
                }
            }
        }
    }

    /**
     * 
     * @param geom 
     * @param callback 
     * @param owner 
     */
    extractMidPoints(geom: any[], callback: Function, owner?: any) {
        this.extractLines(geom, (array: number[], startIndex: number, endIndex: number, ringIndex: number, polygonIndex: number) => {
            while(startIndex < endIndex - 2){ // last point is the same as the first point in polygon
                const midPointStartIndex = startIndex;
                const x1 = array[startIndex++];
                const y1 = array[startIndex++];
                const x2 = array[startIndex];
                const y2 = array[startIndex + 1];
                const midPoint = [(x1 + x2) / 2, (y1 + y2) / 2];

                callback.call(owner, midPoint, midPointStartIndex, ringIndex, polygonIndex);
            }
        });
    }
    
    /**
     * 
     * @param geom 
     * @param callback 
     * @param owner 
     */
    extractLines(geom: any[], callback: Function, owner?: any) {
        for (let g = geom.length; g-- > 1; ) {
            const polygon = geom[g];

            for (let p = polygon.length; p-- > 0; ) {
                const ring = polygon[p];

                callback.call(owner, ring, 0, ring.length, p, g);
            }
        }
    }
    
    /**
     * 
     * @param geom 
     * @param bounds 
     * 
     * @returns 
     */
    getBounds(geom: any[], bounds: number[]): number[] {
        for (let g = 1; g < geom.length; g++) {
            const polygon = geom[g];

            for (let p = polygon.length; p-- > 0; ) {
                bounds = this.boundsUtil.valueOf(polygon[p], bounds);
            }
        }

        return bounds;
    }
    
    /**
     * 
     * @param geom 
     * @param matrix 
     * 
     * @returns 
     */
    transform(geom: any[], matrix: any[]): any[] {
        const ret: any[] = ["MP"];
        for (let g = 1; g < geom.length; g++) {
            const polygon = geom[g];

            const transformedPolygon = [];
            for (let p = 0; p < polygon.length; p++) {
                const ring = polygon[p];

                transformedPolygon[p] = this.matrixUtil.transform(matrix, ring, 0, ring.length);
            }

            ret[g] = transformedPolygon;
        }

        return ret;
    }
    
    /**
     * 
     * @param geom 
     * @param threshold 
     * 
     * @returns 
     */
    generalize(geom: any[], threshold: number): any[] {
        const ret: any[] = ["MP"];
        for (let g = 1; g < geom.length; g++) {
            const polygon = geom[g];

            const polyOut = [];
            for (let p = 0; p < polygon.length; p++) {
                polyOut.push(this.geomUtil.generalizeLines(polygon[p], 0, null, threshold));
            }

            ret.push(polyOut);
        }
        
        return ret;
    }
}
